import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

export const Catalogs = () => {
  const [catalogs, setCatalogs] = useState({
    insurers: [],
    policy_types: [],
    currencies: [],
    payment_frequencies: [],
    payment_methods: [],
    statuses: []
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentCatalog, setCurrentCatalog] = useState('insurers');
  const [activeTab, setActiveTab] = useState('insurers');
  const [newItemName, setNewItemName] = useState('');

  useEffect(() => {
    initializeCatalogs();
  }, []);

  const initializeCatalogs = async () => {
    try {
      await api.post('/catalogs/initialize');
      fetchAllCatalogs();
    } catch (error) {
      fetchAllCatalogs();
    }
  };

  const fetchAllCatalogs = async () => {
    setLoading(true);
    try {
      const types = ['insurers', 'policy_types', 'currencies', 'payment_frequencies', 'payment_methods', 'statuses'];
      const results = await Promise.all(
        types.map(type => api.get(`/catalogs/${type}`))
      );
      
      const newCatalogs = {};
      types.forEach((type, index) => {
        newCatalogs[type] = results[index].data;
      });
      
      setCatalogs(newCatalogs);
    } catch (error) {
      toast.error('Error al cargar catálogos');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (catalogType) => {
    setCurrentCatalog(catalogType);
    setEditingItem(null);
    setNewItemName('');
    setDialogOpen(true);
  };

  const handleEdit = (catalogType, item) => {
    setCurrentCatalog(catalogType);
    setEditingItem(item);
    setNewItemName(item.name);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!newItemName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }

    try {
      if (editingItem) {
        await api.put(`/catalogs/${currentCatalog}/${editingItem.id}`, {
          name: newItemName
        });
        toast.success('Elemento actualizado exitosamente');
      } else {
        await api.post(`/catalogs/${currentCatalog}`, {
          name: newItemName
        });
        toast.success('Elemento agregado exitosamente');
      }
      
      setDialogOpen(false);
      fetchAllCatalogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (catalogType, itemId) => {
    if (!window.confirm('¿Estás seguro de eliminar este elemento?')) return;

    try {
      await api.delete(`/catalogs/${catalogType}/${itemId}`);
      toast.success('Elemento eliminado exitosamente');
      fetchAllCatalogs();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getCatalogTitle = (type) => {
    const titles = {
      insurers: 'Aseguradoras',
      policy_types: 'Tipos de Póliza',
      currencies: 'Monedas',
      payment_frequencies: 'Frecuencia de Pago',
      payment_methods: 'Métodos de Pago',
      statuses: 'Estatus'
    };
    return titles[type] || type;
  };

  const renderCatalogList = (catalogType) => {
    const items = catalogs[catalogType] || [];

    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-medium text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {getCatalogTitle(catalogType)}
          </h3>
          <Button
            data-testid={`add-${catalogType}-button`}
            onClick={() => handleAdd(catalogType)}
            size="sm"
            className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white"
          >
            <Plus size={16} className="mr-1" />
            Agregar
          </Button>
        </div>

        {items.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No hay elementos en este catálogo</p>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`catalog-item-${item.id}`}
                className="flex items-center justify-between p-3 bg-white border border-[#e0e0e0] rounded-[10px] hover:border-[#2537b0]/50 transition-colors"
              >
                <span className="text-sm font-medium text-slate-900">{item.name}</span>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-${item.id}`}
                    onClick={() => handleEdit(catalogType, item)}
                    variant="ghost"
                    size="sm"
                    className="text-[#2537b0] hover:bg-blue-50"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    data-testid={`delete-${item.id}`}
                    onClick={() => handleDelete(catalogType, item.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Catálogos
        </h1>
        <p className="text-sm leading-relaxed text-slate-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
          Gestiona los catálogos del sistema
        </p>
      </div>

      <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="insurers" data-testid="tab-insurers">Aseguradoras</TabsTrigger>
            <TabsTrigger value="policy_types" data-testid="tab-policy-types">Tipos</TabsTrigger>
            <TabsTrigger value="currencies" data-testid="tab-currencies">Monedas</TabsTrigger>
            <TabsTrigger value="payment_frequencies" data-testid="tab-payment-frequencies">Frecuencia</TabsTrigger>
            <TabsTrigger value="payment_methods" data-testid="tab-payment-methods">Métodos</TabsTrigger>
            <TabsTrigger value="statuses" data-testid="tab-statuses">Estatus</TabsTrigger>
          </TabsList>

          <TabsContent value="insurers">{renderCatalogList('insurers')}</TabsContent>
          <TabsContent value="policy_types">{renderCatalogList('policy_types')}</TabsContent>
          <TabsContent value="currencies">{renderCatalogList('currencies')}</TabsContent>
          <TabsContent value="payment_frequencies">{renderCatalogList('payment_frequencies')}</TabsContent>
          <TabsContent value="payment_methods">{renderCatalogList('payment_methods')}</TabsContent>
          <TabsContent value="statuses">{renderCatalogList('statuses')}</TabsContent>
        </Tabs>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Editar' : 'Agregar'} {getCatalogTitle(currentCatalog)}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifica el nombre del elemento' : 'Agrega un nuevo elemento al catálogo'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="item-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Nombre
            </Label>
            <Input
              id="item-name"
              data-testid="catalog-item-name-input"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Nombre del elemento"
              className="mt-1"
              onKeyPress={(e) => e.key === 'Enter' && handleSave()}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="save-catalog-item-button"
              onClick={handleSave}
              className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
